/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/types/risks.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import { useQuery } from '@apollo/client/react';
import i18n from 'i18next';
import { NamedItemType } from 'types/graphql-global-types';
import { FieldChangeGrid } from 'views/pages/admin/audit-log/change-grid';
import AuditEnumValues from 'views/pages/admin/audit-log/definitions/enums';
import {
    getFieldChangeItems,
    getProcessedFieldChangeItems,
    removePropertiesFromObject,
    RiqAssociationConfig,
} from 'views/pages/admin/audit-log/service';
import { GET_NAMED_ITEMS_BY_TYPE } from '@queries/common';
import { getNamedItemsByType } from '@queries/generated/common.generated';
import { RiskAuditFields } from '@queries/generated/fragments.generated';

const propArray = ['Active'];

const RiskRecordChangeGrid = ({ newItem, oldItem, oldRecord, newRecord, record }: any) => {
    const refactoredOldRecord =
        oldRecord && Object.keys(oldRecord).length
            ? {
                  ...oldRecord,
                  ...removePropertiesFromObject(oldRecord, propArray),
                  ReviewFrequencyPeriod: oldRecord.ReviewFrequencyUnits,
                  ReviewFrequencyUnits: oldRecord.ReviewFrequencyPeriod,
              }
            : oldRecord;
    const refactoredNewRecord = newRecord
        ? {
              ...removePropertiesFromObject(newRecord, propArray),
              ReviewFrequencyPeriod: newRecord.ReviewFrequencyUnits,
              ReviewFrequencyUnits: newRecord.ReviewFrequencyPeriod,
          }
        : newRecord;

    const { data: vendorData } = useQuery<getNamedItemsByType>(GET_NAMED_ITEMS_BY_TYPE, {
        variables: {
            type: NamedItemType.VENDOR,
            timestamp: record.created,
        },
        fetchPolicy: 'no-cache',
    });

    const enums: any = {
        ReviewFrequencyPeriod: AuditEnumValues.TimeFrequency,
        Status: AuditEnumValues.RiskStatus,
        RiqStatus: AuditEnumValues.RiqStatus,
    };

    const lookups: any = {
        OwnerId: (risk: RiskAuditFields) => (risk?.owner ? `${risk.owner.firstName} ${risk.owner.lastName}` : ' '),
        ControlStrategyId: (risk: RiskAuditFields) => risk?.controlStrategy?.name,
        CategoryId: (risk: RiskAuditFields) => risk?.category?.name,
        ResponsibleFunctionId: (risk: RiskAuditFields) => risk?.responsibleFunction?.name,
        QualityIssueId: (risk: RiskAuditFields) => risk?.qualityIssue?.name,
    };

    const associations: any = {
        Controls: {
            itemField: 'controls',
            name: 'Control',
            nameResolver: (control: any) => control.description,
        },
        AssociatedVariables: {
            itemField: 'associatedVariables',
            name: 'Critical to Quality Factor',
            nameResolver: (cv: any) => cv.description,
        },
        ObservationIds: {
            itemField: 'observations',
            name: 'Observation',
            nameResolver: (observation: any) => observation.description,
        },
        EvaluationHistoryIds: {
            itemField: 'evaluationHistory',
            name: 'Evaluation',
            customMessage: 'Risk Evaluation Added',
            nameResolver: (evaluation: any) => `Impact: ${evaluation.impact}; Likelihood: ${evaluation.likelihood}; 
                Detectability: ${evaluation.detectability}; 
                Justification for Change: ${evaluation.changeReason}
                Rationale: ${evaluation.rationale}`,
        },
        AssociatedControlsHubPanels: {
            itemField: 'controlsHubPanels',
            name: 'Controls Hub Panel',
            nameResolver: (p: any) => p.plotTitle,
        },
    };

    const riqAssociations: RiqAssociationConfig = {
        statusByField: {
            RiqPendingVariableAssociations: 'Pending',
            RiqAcceptedVariableAssociations: 'Accepted',
            RiqRejectedVariableAssociations: 'Rejected',
            RiqAcceptedAndPromotedVariableAssociations: 'AcceptedAndPromoted',
        },
        lookupField: 'associatedRiqVariables',
        target: 'Ctq',
        nameResolver: (cv: any) =>
            cv?.description
                ? i18n.t('auditLog:riqCtqName', { recordId: cv.recordId, description: cv.description })
                : null,
    };

    const formatCountries = (recordData: { Code: string; Name: string }, index: number) => {
        if (!recordData) {
            return null;
        }

        return `Order: ${index + 1}\nName: ${recordData?.Name}`;
    };

    const formatVendorIds = (recordData: any, index: number) => {
        if (!recordData) {
            return null;
        }

        const data = recordData as { $oid: string };
        const vendor = vendorData?.namedItemsByType?.find((rv) => rv?.id === data.$oid);

        if (!vendor) {
            return null;
        }

        return `Order: ${index + 1}\n RecordId: ${vendor?.recordId}\nName: ${vendor?.name}`;
    };

    const arrayOfObjectsV2: any = {
        Countries: {
            valueResolver: formatCountries,
            compareField: 'Code',
        },
        VendorIds: {
            valueResolver: formatVendorIds,
            compareField: '$oid',
        },
    };

    const items = getFieldChangeItems(refactoredOldRecord, refactoredNewRecord, null, (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
            enums,
            lookups,
            associations,
            arrayOfObjectsV2,
            riqAssociations,
        }),
    );

    return <FieldChangeGrid items={items} />;
};

export { RiskRecordChangeGrid };
